import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Building2, Mail, Phone, MapPin, AlertCircle, Globe, Hash, FileText, ChevronRight, ChevronLeft, User } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { authAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';

const RegisterCompany = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    // Étape 1: Organisation
    companyName: '',
    companyEmail: '',
    formeJuridique: '',
    siret: '',
    codeNAF: '',
    numeroTVA: '',
    numeroCNAPS: '',
    
    // Étape 2: Contact & Adresse
    companyPhone: '',
    website: '',
    address: '',
    complementAddress: '',
    codePostal: '',
    ville: '',
    pays: 'France',
    timezone: 'Europe/Paris',
    
    // Étape 3: Administrateur
    subscription: 'enterprise',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!formData.companyName || !formData.companyEmail || !formData.formeJuridique) {
        toast({ title: "Erreur", description: "Veuillez remplir les champs obligatoires (*)", variant: "destructive" });
        return false;
      }
      // Validation email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.companyEmail)) {
        toast({ title: "Erreur", description: "Email invalide", variant: "destructive" });
        return false;
      }
    } else if (currentStep === 2) {
      if (!formData.address || !formData.codePostal || !formData.ville || !formData.pays) {
        toast({ title: "Erreur", description: "Veuillez remplir l'adresse complète", variant: "destructive" });
        return false;
      }
    } else if (currentStep === 3) {
      if (!formData.adminName || !formData.adminEmail || !formData.adminPassword) {
        toast({ title: "Erreur", description: "Veuillez remplir tous les champs administrateur", variant: "destructive" });
        return false;
      }
      if (formData.adminPassword !== formData.confirmPassword) {
        toast({ title: "Erreur", description: "Les mots de passe ne correspondent pas", variant: "destructive" });
        return false;
      }
      if (formData.adminPassword.length < 8) {
        toast({ title: "Erreur", description: "Le mot de passe doit contenir au moins 8 caractères", variant: "destructive" });
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(3)) return;

    setLoading(true);

    try {
      const companyData = {
        name: formData.companyName,
        email: formData.companyEmail,
        phone: formData.companyPhone,
        address: `${formData.address}${formData.complementAddress ? ', ' + formData.complementAddress : ''}, ${formData.codePostal} ${formData.ville}, ${formData.pays}`,
        subscription: formData.subscription,
        formeJuridique: formData.formeJuridique,
        siret: formData.siret,
        codeNAF: formData.codeNAF,
        numeroTVA: formData.numeroTVA,
        numeroCNAPS: formData.numeroCNAPS,
        website: formData.website,
        ville: formData.ville,
        codePostal: formData.codePostal,
        pays: formData.pays,
        timezone: formData.timezone
      };

      const adminData = {
        name: formData.adminName,
        email: formData.adminEmail,
        password: formData.adminPassword
      };

      const result = await authAPI.register(companyData, adminData);

      toast({ title: "✅ Société créée", description: "Votre société a été inscrite avec succès ! Vous pouvez maintenant vous connecter." });
      navigate('/login');

    } catch (error) {
      console.error('Registration error:', error);
      toast({ title: "Erreur", description: getErrorMessage(error, "Impossible de créer la société"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Registration form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Shield className="w-12 h-12 text-blue-600 mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">HIGH TECH SECURITY</h1>
          </div>
          <p className="text-gray-600">Créez votre organisation en quelques étapes</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between items-center mb-8 px-4">
          <div className={`flex flex-col items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${step >= 1 ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'}`}>
              <Building2 className="w-5 h-5" />
            </div>
            <span className="text-xs mt-2 font-medium">Organisation</span>
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
          <div className={`flex flex-col items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${step >= 2 ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'}`}>
              <MapPin className="w-5 h-5" />
            </div>
            <span className="text-xs mt-2 font-medium">Adresse</span>
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
          <div className={`flex flex-col items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${step >= 3 ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white'}`}>
              <User className="w-5 h-5" />
            </div>
            <span className="text-xs mt-2 font-medium">Admin</span>
          </div>
        </div>

        <Card className="shadow-xl">
          <form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle className="text-2xl">
                {step === 1 && "Informations de votre organisation"}
                {step === 2 && "Contact et adresse"}
                {step === 3 && "Compte administrateur principal"}
              </CardTitle>
              <CardDescription>
                {step === 1 && "Les informations ci-dessous seront utilisées pour la génération des devis, factures et avoirs."}
                {step === 2 && "Où se trouve votre organisation ?"}
                {step === 3 && "Creez le compte administrateur principal qui gerera la societe."}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Step 1: Organization Info */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyEmail">Adresse email professionnelle *</Label>
                    <Input 
                      id="companyEmail" 
                      name="companyEmail" 
                      type="email" 
                      placeholder="contact@votresociete.fr" 
                      value={formData.companyEmail} 
                      onChange={handleInputChange} 
                      required 
                    />
                    <p className="text-xs text-gray-500">Cette adresse apparaîtra sur tous vos documents</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyName">Nom de l&apos;organisation *</Label>
                    <Input 
                      id="companyName" 
                      name="companyName" 
                      placeholder="SECUTOR PRO LLC" 
                      value={formData.companyName} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="formeJuridique">Forme juridique *</Label>
                    <select
                      id="formeJuridique"
                      name="formeJuridique"
                      value={formData.formeJuridique}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionnez une forme juridique</option>
                      <option value="SARL">SARL</option>
                      <option value="SAS">SAS</option>
                      <option value="SASU">SASU</option>
                      <option value="EURL">EURL</option>
                      <option value="SA">SA</option>
                      <option value="SNC">SNC</option>
                      <option value="EI">Entreprise Individuelle</option>
                      <option value="Auto-entrepreneur">Auto-entrepreneur</option>
                      <option value="Association">Association</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="siret">SIREN, SIRET…</Label>
                      <Input 
                        id="siret" 
                        name="siret" 
                        placeholder="123 456 789 00012" 
                        value={formData.siret} 
                        onChange={handleInputChange} 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="codeNAF">Code NAF, NACE…</Label>
                      <Input 
                        id="codeNAF" 
                        name="codeNAF" 
                        placeholder="8010Z" 
                        value={formData.codeNAF} 
                        onChange={handleInputChange} 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="numeroTVA">Numéro de TVA</Label>
                      <Input 
                        id="numeroTVA" 
                        name="numeroTVA" 
                        placeholder="FR 12 345678901" 
                        value={formData.numeroTVA} 
                        onChange={handleInputChange} 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="numeroCNAPS">Numéro CNAPS</Label>
                      <Input 
                        id="numeroCNAPS" 
                        name="numeroCNAPS" 
                        placeholder="AGD-XXX-XXXX-XX-XX-XXXXXXXX" 
                        value={formData.numeroCNAPS} 
                        onChange={handleInputChange} 
                      />
                      <p className="text-xs text-gray-500">Obligatoire pour le secteur de la sécurité privée</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                    <p className="text-sm text-blue-900">
                      💡 <strong>Astuce :</strong> Vous pourrez modifier ces informations à tout moment depuis Paramètres → Coordonnées
                    </p>
                  </div>
                </div>
              )}

              {/* Step 2: Address & Contact */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="address">Adresse *</Label>
                    <Input 
                      id="address" 
                      name="address" 
                      placeholder="123 Rue de la Securite" 
                      value={formData.address} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="complementAddress">Complement d&apos;adresse</Label>
                    <Input 
                      id="complementAddress" 
                      name="complementAddress" 
                      placeholder="Bâtiment A, Étage 3" 
                      value={formData.complementAddress} 
                      onChange={handleInputChange} 
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="codePostal">Code postal *</Label>
                      <Input 
                        id="codePostal" 
                        name="codePostal" 
                        placeholder="75001" 
                        value={formData.codePostal} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ville">Ville *</Label>
                      <Input 
                        id="ville" 
                        name="ville" 
                        placeholder="Paris" 
                        value={formData.ville} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pays">Pays *</Label>
                    <select
                      id="pays"
                      name="pays"
                      value={formData.pays}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="France">France</option>
                      <option value="Belgique">Belgique</option>
                      <option value="Suisse">Suisse</option>
                      <option value="Luxembourg">Luxembourg</option>
                      <option value="Monaco">Monaco</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Fuseau horaire</Label>
                    <select
                      id="timezone"
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Europe/Paris">Paris (UTC+1)</option>
                      <option value="Europe/Brussels">Brussels (UTC+1)</option>
                      <option value="Europe/Zurich">Zurich (UTC+1)</option>
                      <option value="Europe/Luxembourg">Luxembourg (UTC+1)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyPhone">Numéro de téléphone</Label>
                      <Input 
                        id="companyPhone" 
                        name="companyPhone" 
                        type="tel" 
                        placeholder="+33 1 23 45 67 89" 
                        value={formData.companyPhone} 
                        onChange={handleInputChange} 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Site internet</Label>
                      <Input 
                        id="website" 
                        name="website" 
                        type="url" 
                        placeholder="https://www.votresociete.fr" 
                        value={formData.website} 
                        onChange={handleInputChange} 
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Administrator Account */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded mb-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                      <div className="text-sm text-blue-900">
                        <p className="font-semibold mb-1">Vous etes l&apos;administrateur principal</p>
                        <p>Un code societe unique sera genere. Partagez-le avec vos collaborateurs pour qu&apos;ils puissent creer leurs comptes.</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adminName">Nom complet *</Label>
                    <Input 
                      id="adminName" 
                      name="adminName" 
                      placeholder="Jean Dupont" 
                      value={formData.adminName} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adminEmail">Email professionnel *</Label>
                    <Input 
                      id="adminEmail" 
                      name="adminEmail" 
                      type="email" 
                      placeholder="jean.dupont@votresociete.fr" 
                      value={formData.adminEmail} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="adminPassword">Mot de passe *</Label>
                      <Input 
                        id="adminPassword" 
                        name="adminPassword" 
                        type="password" 
                        placeholder="Minimum 8 caractères" 
                        value={formData.adminPassword} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                      <Input 
                        id="confirmPassword" 
                        name="confirmPassword" 
                        type="password" 
                        placeholder="Confirmer" 
                        value={formData.confirmPassword} 
                        onChange={handleInputChange} 
                        required 
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded">
                    <p className="text-sm text-gray-700">
                      En creant un compte, vous acceptez nos conditions d&apos;utilisation et notre politique de confidentialite.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex justify-between">
              {step > 1 && (
                <Button type="button" variant="outline" onClick={handleBack} disabled={loading}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Précédent
                </Button>
              )}
              
              {step < 3 ? (
                <Button type="button" onClick={handleNext} className="ml-auto" disabled={loading}>
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button type="submit" className="ml-auto bg-green-600 hover:bg-green-700" disabled={loading}>
                  {loading ? 'Création en cours...' : 'Créer ma société'}
                </Button>
              )}
            </CardFooter>
          </form>
        </Card>

        <div className="text-center mt-6">
          <Link to="/login" className="text-sm text-blue-600 hover:underline">
            Deja inscrit ? Se connecter
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterCompany;
